import * as React from 'react';

import { toZoned } from '@internationalized/date';
import {
  CurrencyCode,
  enumPaymentMethod,
  enumPaymentType,
  PaymentMethod,
  PaymentType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  Col,
  Row,
  DatePicker,
  TextArea,
  TextField,
  NumberField,
  type DateValue,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';

import * as formState from '../../_state/paymentForm.state';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectFinancialAccount } from '~/shared/components/select-fields/SelectFinancialAccount';
import { store } from '~/shared/state';
import { $localization } from '~/shared/state/preferences.state';

type PaymentWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const PaymentWizard = ({
  isOpen,
  onClose,
  onSuccess,
}: PaymentWizardProps) => {
  const toast = useToast();
  const t = useTranslations();

  const [form] = useAtom(formState.$paymentForm);
  const [isSubmitting, setIsSubmitting] = useAtom(formState.$isSubmitting);
  const [currencyCode, setCurrencyCode] = useAtom(formState.$currencyCode);
  const updateForm = useSetAtom(formState.$updatePaymentForm);
  const resetForm = useSetAtom(formState.$resetPaymentForm);
  const validateForm = useSetAtom(formState.$validatePaymentForm);
  const createPayment = useSetAtom(formState.$createPayment);
  const isMissingRequired = useAtomValue(formState.$paymentIsMissingRequired);
  const nextDocumentNumberMutation = useSetAtom(
    formState.$nextPaymentDocumentNumber,
  );
  const { data: nextCheckNumber } = useAtomValue(formState.$nextCheckNumber);
  const [documentNumber, setDocumentNumber] = React.useState<string>('');
  const isCheckPayment = form.paymentMethod === 'Check';

  React.useEffect(() => {
    if (!isOpen || documentNumber) return;

    const abortController = new AbortController();
    nextDocumentNumberMutation({})
      .then((data) => {
        if (abortController.signal.aborted) return;
        setDocumentNumber(data.financeNextPaymentDocumentNumber);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        console.error('Failed to get next payment document number:', error);
      });
    return () => {
      abortController.abort();
    };
  }, [isOpen, documentNumber, nextDocumentNumberMutation]);

  React.useEffect(() => {
    if (isCheckPayment && form.financialAccountId && nextCheckNumber) {
      updateForm({ checkNumber: nextCheckNumber });
    }
  }, [isCheckPayment, form.financialAccountId, nextCheckNumber, updateForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    setDocumentNumber('');
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = React.useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      toast.error(errorMessages);
      return;
    }

    try {
      setIsSubmitting(true);

      if (!form.amount || !form.transactionDate || !form.financialAccountId)
        return;

      await createPayment({
        paymentType: form.paymentType,
        paymentMethod: form.paymentMethod,
        documentNumber: documentNumber || undefined,
        amount: { amount: form.amount.amount, currencyCode },
        transactionDate: form.transactionDate,
        accountId: form.accountId || undefined,
        financialAccountId: form.financialAccountId,
        checkFacilityId: form.checkFacilityId || undefined,
        checkNumber: form.checkNumber || undefined,
        checkMemo: form.checkMemo || undefined,
        achNumber: form.achNumber || undefined,
        wireNumber: form.wireNumber || undefined,
      });

      toast.success(t.payments.wizard.messages.createSuccess);
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(
        t.payments.wizard.messages.createError(
          error instanceof Error
            ? error.message
            : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    toast,
    setIsSubmitting,
    createPayment,
    form,
    documentNumber,
    currencyCode,
    onSuccess,
    handleClose,
  ]);

  const handleAmountChange = React.useCallback(
    (value: number | undefined) => {
      updateForm({
        amount:
          value != null
            ? { amount: value, currencyCode, __typename: 'Money' as const }
            : null,
      });
    },
    [updateForm, currencyCode],
  );

  return (
    <ConsoleDialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@PaymentWizard"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>{t.payments.list.buttons.create}</span>
          </Row>
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={
        <div className="grid grid-cols-2 gap-4 p-4">
          <TextField
            label={t.payments.wizard.fields.documentNumber}
            value={documentNumber}
            onChange={setDocumentNumber}
            placeholder={t.common.placeholders.generateNumber}
          />
          <SelectEnum<PaymentType>
            label={t.payments.wizard.fields.paymentType}
            enumValue={enumPaymentType}
            value={form.paymentType}
            onChange={(value) => updateForm({ paymentType: value })}
            formatOptionLabel={(value) => t.enums.paymentType[value] ?? value}
            isRequired
          />
          <SelectEnum<PaymentMethod>
            label={t.payments.wizard.fields.paymentMethod}
            enumValue={enumPaymentMethod}
            value={form.paymentMethod}
            onChange={(value) => {
              if (value) {
                updateForm({ paymentMethod: value });
              }
            }}
            isRequired
          />
          <NumberField
            label={t.payments.wizard.fields.amount}
            value={form.amount?.amount ?? undefined}
            onChange={handleAmountChange}
            formatOptions={{ style: 'currency', currency: currencyCode }}
            isRequired
          />
          <SelectCurrency
            label={t.payments.wizard.fields.currency}
            value={currencyCode}
            onChange={(value) => setCurrencyCode(value as CurrencyCode)}
            isRequired
          />
          <DatePicker
            label={t.payments.wizard.fields.transactionDate}
            value={form.transactionDate as DateValue | undefined}
            onChange={(value) => {
              if (!value) {
                updateForm({ transactionDate: null });
                return;
              }
              const localTimezone = store.get($localization).timezone ?? 'UTC';
              const zonedDateTime = toZoned(value, localTimezone);
              updateForm({ transactionDate: zonedDateTime });
            }}
            isRequired
          />
          <SelectAccount
            label={t.payments.wizard.fields.account}
            value={form.accountId ?? undefined}
            onChange={(value) => updateForm({ accountId: value ?? null })}
          />
          <SelectFinancialAccount
            label={t.payments.wizard.fields.financialAccount}
            value={form.financialAccountId ?? undefined}
            onChange={(value) =>
              updateForm({ financialAccountId: value ?? null })
            }
            visibleInPayments={true}
          />
          {isCheckPayment && (
            <>
              <SelectFacility
                label={t.payments.wizard.fields.checkFacility}
                value={form.checkFacilityId ?? undefined}
                onChange={(value) =>
                  updateForm({ checkFacilityId: value ?? null })
                }
              />
              <TextField
                label={t.payments.wizard.fields.checkNumber}
                value={form.checkNumber ?? ''}
                onChange={(value) => updateForm({ checkNumber: value || null })}
              />
              <div className="col-span-2">
                <TextArea
                  label={t.payments.wizard.fields.checkMemo}
                  value={form.checkMemo ?? ''}
                  onChange={(value) => updateForm({ checkMemo: value || null })}
                  rows={2}
                />
              </div>
            </>
          )}
          {form.paymentMethod === 'ACH' && (
            <TextField
              label={t.payments.wizard.fields.achNumber}
              value={form.achNumber ?? ''}
              onChange={(value) => updateForm({ achNumber: value || null })}
            />
          )}
          {form.paymentMethod === 'Wire' && (
            <TextField
              label={t.payments.wizard.fields.wireNumber}
              value={form.wireNumber ?? ''}
              onChange={(value) => updateForm({ wireNumber: value || null })}
            />
          )}
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-4">
          <Button
            color="neutral"
            variant="outline"
            onPress={handleClose}
            isPending={isSubmitting}
          >
            {t.payments.wizard.buttons.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            onPress={handleSubmit}
            isPending={isSubmitting}
            isLoading={isSubmitting}
            isDisabled={isMissingRequired}
          >
            {t.payments.wizard.buttons.create}
          </Button>
        </div>
      }
    />
  );
};
